import React from 'react';

import { List } from 'react-admin';

import {
  Box,
  Card,
  LinearProgress,
} from '@mui/material';

import useRuleType from '../../hooks/useRule';
import useRuleVersion from '../../hooks/useRuleVersion';
import usePendingReview from '../../hooks/usePendingReview';
import actions from '../../constants/actions';
import { PendingReviewProvider } from '../../context/PendingReviewContext';

import RulesActions from './components/actions';
import RuleDatagrid from './components/RuleDatagrid';
import RuleExpand, { GetList, OpenIconButton } from './RuleExpand';
import TreeView from './components/treeView';
import PendingReviewBanner from './components/actions/PendingReviewBanner';

import ruleTypes, { RuleTypes } from '../../constants/ruleTypes';
import ruleViews, { RuleViews } from '../../constants/ruleView';
import { Rule } from '../../utilities/schemas/rules';

import Pagination from '../layout/Pagination';
import { listViewDatagridStyle } from '../../constants/style/datagridStyles';

const RuleListBody = ({
  ruleType,
  view,
  isDraft,
  version,
}: {
  ruleType: RuleTypes;
  view: RuleViews;
  isDraft: boolean;
  version: string;
}) => {
  switch (view) {
    case ruleViews.LIST:
      switch (ruleType) {
        case ruleTypes.REQUEST:
          return (
            <RuleDatagrid
              expand={<RuleExpand ruleType={ruleType} version={version} isDraft={isDraft} />}
              isRowExpandable={(row: Rule) => GetList(row.id, row.ruleType, version, isDraft)}
              ruleType={ruleType}
              sx={listViewDatagridStyle}
            >
              <OpenIconButton isDraft={isDraft} ruleType={ruleType} version={version} />
            </RuleDatagrid>
          );
        case ruleTypes.ONGOING:
          return (
            <RuleDatagrid
              expand={<RuleExpand ruleType={ruleType} version={version} isDraft={isDraft} />}
              isRowExpandable={(row: Rule) => GetList(row.id, row.ruleType, version, isDraft)}
              ruleType={ruleType}
              sx={listViewDatagridStyle}
            >
              <OpenIconButton isDraft={isDraft} ruleType={ruleType} version={version} />
            </RuleDatagrid>
          );
        default:
          return null;
      }
    case ruleViews.TREE:
      return (
        <TreeView
          ruleType={ruleType}
          isDraft={isDraft}
          version={version}
        />
      );
    default:
      return null;
  }
};

const RuleList = () => {
  const {
    ruleType,
    changeRuleType,
    view,
    changeView,
  } = useRuleType();

  const {
    version,
    record: versionRecord,
    status: versionStatus,
    latestLiveVersionId,
    changeVersion,
    resetToLatestVersion,
    selectLatestVersion,
    refetchVersion,
    isDraft,
    isLoading: isVersionLoading,
  } = useRuleVersion(ruleType);
  const {
    pendingReview: publishReview,
    refetch: refetchPublishReview,
  } = usePendingReview(actions.RULE_VERSION_PUBLISH, { ruleType });
  const isTestVersion = !!versionRecord?.isTestVersion;
  const {
    pendingReview: releaseReview,
    refetch: refetchReleaseReview,
  } = usePendingReview(
    actions.RULE_VERSION_RELEASE,
    { ruleVersion: version },
    { enabled: !isDraft && isTestVersion },
  );

  return (
    <PendingReviewProvider value={!!publishReview && isDraft}>
      <List
        perPage={1000}
        pagination={(
          <Pagination
            disableEmptyText
            hidden={view === ruleViews.TREE}
          />
        )}
        filter={{
          ruleType,
          version: isDraft ? null : version,
          ...(view === ruleViews.LIST ? { triggeredBy: 'none' } : {}),
        }}
        empty={false}
        queryOptions={{ enabled: !isVersionLoading }}
        actions={(
          <RulesActions
            view={view}
            ruleType={ruleType}
            changeRuleType={changeRuleType}
            changeView={changeView}
            version={version}
            versionRecord={versionRecord}
            versionStatus={versionStatus}
            latestLiveVersionId={latestLiveVersionId}
            changeVersion={changeVersion}
            resetToLatestVersion={resetToLatestVersion}
            selectLatestVersion={selectLatestVersion}
            refetchVersion={refetchVersion}
            isDraft={isDraft}
            isVersionLoading={isVersionLoading}
          />
        )}
        exporter={false}
        sx={view === ruleViews.TREE ? {
          height: '100%',
          '& .RaList-content': {
            height: '100%',
            marginBottom: 0,
          },
        } : undefined}
        component="div"
      >
        <Box display="flex" flexDirection="column" height="100%" gap={2}>
          {publishReview
          && isDraft
          && (
            <PendingReviewBanner
              review={publishReview}
              onReviewResolved={refetchPublishReview}
              onReviewApproved={() => selectLatestVersion(false)}
            />
          )}
          {releaseReview
          && !isDraft
          && isTestVersion
          && (
            <PendingReviewBanner
              review={releaseReview}
              onReviewResolved={refetchReleaseReview}
              onReviewApproved={() => selectLatestVersion(false)}
            />
          )}
          <Card sx={{ margin: 0, flexGrow: 1 }} variant="outlined">
            {isVersionLoading
              ? <LinearProgress />
              : (
                <RuleListBody
                  ruleType={ruleType}
                  view={view}
                  isDraft={isDraft}
                  version={version}
                />
              )}
          </Card>
        </Box>
      </List>
    </PendingReviewProvider>
  );
};

export default RuleList;
